import type { CurrentValueConfirmation } from './types';

/**
 * **The one sentence that explains a confirmed figure** — who vouched for it, when,
 * against what, and whether AI helped (VALE-355).
 *
 * Extracted from `current-view-item-row.tsx`, which composed it inline for the
 * inventory list's ✓ tooltip. The drawer needs the identical sentence for the same
 * hover on the same fact, and a lawyer moving between the list and the drawer must
 * not be told two different stories about one figure — so this is the single
 * definition rather than a second one that starts out matching.
 *
 * Order is the list's, unchanged: the person and the date first (the part a reader
 * hovers *for*), then what the figure was checked against.
 */
export function confirmationProvenanceTip(
  confirmation: CurrentValueConfirmation | null | undefined
): string {
  const at = confirmation ? formatProvenanceDate(confirmation.at) : '';
  return (
    [
      confirmation?.by ? `Confirmed by ${confirmation.by}` : 'Confirmed',
      at ? `on ${at}` : null
    ]
      .filter(Boolean)
      .join(' ') +
    (confirmation?.market ? ' · live market data' : '') +
    (confirmation?.sourceFileName
      ? ` · against ${confirmation.sourceFileName}`
      : '') +
    (confirmation?.source ? ` · ${confirmation.source}` : '') +
    (confirmation?.aiAssisted ? ' · AI-assisted' : '')
  );
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/**
 * Hydration-safe short date, e.g. `20 Aug 2026` — built from the parts rather than
 * a locale format, because a locale-dependent string renders differently on the
 * server and the client and React reports the mismatch.
 */
export function formatProvenanceDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
